import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { useWebsiteData } from '../../../context/WebsiteDataContext';
import { uploadFile } from '../../../services/contentApi';

const ProjectsManager = () => {
  const { getAllProjects, addProject, updateProject, deleteProject, toggleProjectStatus } = useWebsiteData();
  const projects = getAllProjects();
  const [activeTab, setActiveTab] = useState('overview');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    category: 'residential',
    status: 'completed',
    location: '',
    area: '',
    duration: '',
    description: '',
    services: [],
    highlights: [],
    client: '',
    completionDate: '',
    budget: '',
    rating: 5,
    testimonial: '',
    image: '/assets/images/no_image.png',
    gallery: []
  });

  const categories = [
    { value: 'residential', label: 'Residential' },
    { value: 'commercial', label: 'Commercial' },
    { value: 'industrial', label: 'Industrial' },
    { value: 'interior', label: 'Interior Design' }
  ];

  const statuses = [
    { id: 'all', name: 'All Status', count: projects.length },
    { id: 'completed', name: 'Completed', count: projects.filter(p => p.status === 'completed').length },
    { id: 'in-progress', name: 'In Progress', count: projects.filter(p => p.status === 'in-progress').length },
    { id: 'hidden', name: 'Hidden', count: projects.filter(p => p.status === 'hidden').length },
    { id: 'draft', name: 'Draft', count: projects.filter(p => p.status === 'draft').length }
  ];

  const statusOptions = [
    { value: 'completed', label: 'Completed' },
    { value: 'in-progress', label: 'In Progress' },
    { value: 'hidden', label: 'Hidden' },
    { value: 'draft', label: 'Draft' }
  ];

  const serviceOptions = [
    'Architectural Design',
    'Interior Design',
    '3D Visualization',
    'Approval & Liaisoning',
    'Structural Design',
    'MEP Design',
    'Landscape Design',
    'Project Management'
  ];

  const filteredProjects = projects.filter(project => {
    const statusMatch = filterStatus === 'all' || project.status === filterStatus;
    const categoryMatch = filterCategory === 'all' || project.category === filterCategory;
    return statusMatch && categoryMatch;
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleServiceToggle = (service) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(service)
        ? prev.services.filter(s => s !== service)
        : [...prev.services, service]
    }));
  };

  const handleHighlightAdd = () => {
    const highlight = prompt('Enter project highlight:');
    if (highlight && highlight.trim()) {
      setFormData(prev => ({
        ...prev,
        highlights: [...prev.highlights, highlight.trim()]
      }));
    }
  };

  const handleHighlightRemove = (index) => {
    setFormData(prev => ({
      ...prev,
      highlights: prev.highlights.filter((_, i) => i !== index)
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploading(true);
      const result = await uploadFile(file, 'projects');
      setFormData(prev => ({ ...prev, image: result.url }));
    } catch (error) {
      console.error("Upload failed:", error);
      alert(error.message || "Failed to upload image.");
    } finally {
      setUploading(false);
    }
  };

  const handleGalleryUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    try {
      setUploading(true);
      const uploadPromises = files.map(file => uploadFile(file, 'projects/gallery'));
      const results = await Promise.all(uploadPromises);
      const urls = results.map(r => r.url);

      setFormData(prev => ({
        ...prev,
        gallery: [...prev.gallery, ...urls]
      }));
    } catch (error) {
      console.error("Gallery upload failed:", error);
      alert(error.message || "Failed to upload images.");
    } finally {
      setUploading(false);
    }
  };

  const handleGalleryRemove = (index) => {
    setFormData(prev => ({
      ...prev,
      gallery: prev.gallery.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Simple validation
    if (!formData.image || formData.image === '/assets/images/no_image.png') {
      if (!confirm("No image selected. Use default?")) return;
    }

    const projectData = {
      ...formData,
      // If editing, keep ID. If adding, new ID will be generated by API.
      // Do NOT explicitly set ID to Date.now() as it conflicts with Firestore IDs.
      createdAt: editingProject ? editingProject.createdAt : new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      // Ensure numeric values are numbers
      budget: formData.budget, // Keep as string for formatting flexibility or parse if needed
      rating: Number(formData.rating)
    };

    try {
      if (editingProject) {
        // Ensure we pass the ID explicitly to updateProject
        await updateProject(editingProject.id, projectData);
      } else {
        await addProject(projectData);
      }
      handleCloseModal();
    } catch (error) {
      console.error("Save failed:", error);
      alert("Failed to save project.");
    }
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setFormData({
      title: project.title,
      category: project.category,
      status: project.status,
      location: project.location,
      area: project.area,
      duration: project.duration,
      description: project.description,
      services: project.services || [],
      highlights: project.highlights || [],
      client: project.client,
      completionDate: project.completionDate,
      budget: project.budget,
      rating: project.rating || 5,
      testimonial: project.testimonial || '',
      image: project.image || '/assets/images/no_image.png',
      gallery: project.gallery || []
    });
    setShowAddModal(true);
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setEditingProject(null);
    setUploading(false);
    setFormData({
      title: '',
      category: 'residential',
      status: 'completed',
      location: '',
      area: '',
      duration: '',
      description: '',
      services: [],
      highlights: [],
      client: '',
      completionDate: '',
      budget: '',
      rating: 5,
      testimonial: '',
      image: '/assets/images/no_image.png',
      gallery: []
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'in-progress': return 'bg-blue-100 text-blue-800';
      case 'hidden': return 'bg-gray-100 text-gray-800';
      case 'draft': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'residential': return 'Home';
      case 'commercial': return 'Building';
      case 'industrial': return 'Factory';
      case 'interior': return 'Palette';
      default: return 'Building';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Projects Management</h2>
          <p className="text-muted-foreground">Manage your project portfolio and showcase your work</p>
        </div>
        <Button
          variant="default"
          iconName="Plus"
          iconPosition="left"
          onClick={() => setShowAddModal(true)}
        >
          Add New Project
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex space-x-1 bg-muted p-1 rounded-lg">
        {[
          { id: 'overview', name: 'Overview', icon: 'BarChart3' },
          { id: 'projects', name: 'All Projects', icon: 'FolderOpen' },
          { id: 'analytics', name: 'Analytics', icon: 'TrendingUp' }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === tab.id
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
              }`}
          >
            <Icon name={tab.icon} size={16} />
            <span>{tab.name}</span>
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-card p-6 rounded-lg border border-border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Projects</p>
                <p className="text-2xl font-bold text-foreground">{projects.length}</p>
              </div>
              <Icon name="FolderOpen" size={24} className="text-primary" />
            </div>
          </div>

          <div className="bg-card p-6 rounded-lg border border-border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Completed</p>
                <p className="text-2xl font-bold text-foreground">
                  {projects.filter(p => p.status === 'completed').length}
                </p>
              </div>
              <Icon name="CheckCircle" size={24} className="text-green-500" />
            </div>
          </div>

          <div className="bg-card p-6 rounded-lg border border-border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">In Progress</p>
                <p className="text-2xl font-bold text-foreground">
                  {projects.filter(p => p.status === 'in-progress').length}
                </p>
              </div>
              <Icon name="Clock" size={24} className="text-custom-brown" />
            </div>
          </div>

          <div className="bg-card p-6 rounded-lg border border-border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Categories</p>
                <p className="text-2xl font-bold text-foreground">
                  {new Set(projects.map(p => p.category)).size}
                </p>
              </div>
              <Icon name="Tag" size={24} className="text-purple-500" />
            </div>
          </div>
        </div>
      )}

      {/* Projects Tab */}
      {activeTab === 'projects' && (
        <div className="space-y-6">
          {/* Filters */}
          <div className="flex flex-wrap gap-4">
            <Select
              value={filterStatus}
              onChange={(value) => setFilterStatus(value)}
              options={statuses.map(s => ({ value: s.id, label: s.name }))}
              placeholder="Filter by status"
              className="w-48"
            />

            <Select
              value={filterCategory}
              onChange={(value) => setFilterCategory(value)}
              options={[
                { value: 'all', label: 'All Categories' },
                { value: 'residential', label: 'Residential' },
                { value: 'commercial', label: 'Commercial' },
                { value: 'industrial', label: 'Industrial' },
                { value: 'interior', label: 'Interior Design' }
              ]}
              placeholder="Filter by category"
              className="w-48"
            />
          </div>

          {/* Projects Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProjects.map((project) => (
              <div key={project.id} className="bg-card rounded-lg border border-border overflow-hidden">
                <div className="relative h-48 bg-muted">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.src = '/assets/images/no_image.png'; }}
                  />
                  <div className="absolute top-4 left-4 flex space-x-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(project.status)}`}>
                      {project.status === 'completed' ? 'Completed' :
                        project.status === 'in-progress' ? 'In Progress' :
                          project.status === 'hidden' ? 'Hidden' :
                            project.status === 'draft' ? 'Draft' : project.status}
                    </span>
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/20">
                      {project.category.charAt(0).toUpperCase() + project.category.slice(1)}
                    </span>
                  </div>
                  {project.rating && (
                    <div className="absolute top-4 right-4 flex items-center space-x-1 bg-white/90 rounded-full px-2 py-1">
                      <Icon name="Star" size={12} className="text-yellow-500 fill-current" />
                      <span className="text-xs font-medium">{project.rating}</span>
                    </div>
                  )}
                </div>

                <div className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-semibold text-foreground line-clamp-2">{project.title}</h3>
                    <Icon name={getCategoryIcon(project.category)} size={16} className="text-primary flex-shrink-0 ml-2" />
                  </div>

                  <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{project.description}</p>

                  <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                    <div>
                      <span className="text-muted-foreground">Location:</span>
                      <div className="font-medium">{project.location}</div>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Budget:</span>
                      <div className="font-medium">{project.budget}</div>
                    </div>
                  </div>

                  <div className="flex space-x-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Edit"
                      iconPosition="left"
                      onClick={() => handleEdit(project)}
                      className="bg-custom-brown/10 text-custom-brown hover:bg-custom-brown/20 hover:text-custom-brown"
                    >
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      iconName={project.status === 'hidden' ? 'Eye' : 'EyeOff'}
                      iconPosition="left"
                      onClick={() => toggleProjectStatus(project.id)}
                    >
                      {project.status === 'hidden' ? 'Show' : 'Hide'}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="Trash2"
                      iconPosition="left"
                      onClick={() => {
                        if (confirm('Are you sure you want to delete this project?')) {
                          deleteProject(project.id);
                        }
                      }}
                      className="bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-700 hover:bg-red-100"
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredProjects.length === 0 && (
            <div className="text-center py-12">
              <Icon name="FolderOpen" size={64} className="mx-auto text-muted-foreground/50 mb-4" />
              <h3 className="text-xl font-semibold text-foreground mb-2">No projects found</h3>
              <p className="text-muted-foreground">Try adjusting your filters or add a new project</p>
            </div>
          )}
        </div>
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-card p-6 rounded-lg border border-border">
              <h3 className="text-lg font-semibold text-foreground mb-4">Projects by Category</h3>
              <div className="space-y-3">
                {categories.map((category) => {
                  const count = projects.filter(p => p.category === category.value).length;
                  const percentage = projects.length > 0 ? (count / projects.length * 100).toFixed(1) : 0;
                  return (
                    <div key={category.value} className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Icon name={getCategoryIcon(category.value)} size={16} className="text-primary" />
                        <span className="text-sm font-medium">{category.label}</span>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-semibold">{count}</div>
                        <div className="text-xs text-muted-foreground">{percentage}%</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="bg-card p-6 rounded-lg border border-border">
              <h3 className="text-lg font-semibold text-foreground mb-4">Projects by Status</h3>
              <div className="space-y-3">
                {statuses.map((status) => {
                  const count = projects.filter(p => p.status === status.id).length;
                  const percentage = projects.length > 0 ? (count / projects.length * 100).toFixed(1) : 0;
                  return (
                    <div key={status.id} className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <div className={`w-3 h-3 rounded-full ${status.id === 'completed' ? 'bg-green-500' :
                          status.id === 'in-progress' ? 'bg-blue-500' :
                            status.id === 'hidden' ? 'bg-gray-500' : 'bg-yellow-500'
                          }`}></div>
                        <span className="text-sm font-medium">{status.name}</span>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-semibold">{count}</div>
                        <div className="text-xs text-muted-foreground">{percentage}%</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm"
          onClick={(e) => {
            if (e.target === e.currentTarget) handleCloseModal();
          }}
        >
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full flex flex-col max-h-[90vh] border border-gray-200">
            <div className="p-6 border-b border-gray-200 flex-shrink-0 bg-white rounded-t-lg">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">
                  {editingProject ? 'Edit Project' : 'Add New Project'}
                </h2>
                <button onClick={handleCloseModal} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                  <Icon name="X" size={20} className="text-gray-500" />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col min-h-0 flex-1">
              <div className="p-6 space-y-6 overflow-y-auto flex-1">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Input
                    label="Project Title"
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    placeholder="Enter project title"
                    required
                  />

                  <Select
                    label="Category"
                    value={formData.category}
                    onChange={(value) => handleInputChange('category', value)}
                    options={categories}
                    required
                  />

                  <Select
                    label="Status"
                    value={formData.status}
                    onChange={(value) => handleInputChange('status', value)}
                    options={statusOptions}
                    required
                  />

                  <Input
                    label="Location"
                    value={formData.location}
                    onChange={(e) => handleInputChange('location', e.target.value)}
                    placeholder="e.g., Pune, Maharashtra"
                    required
                  />

                  <Input
                    label="Area"
                    value={formData.area}
                    onChange={(e) => handleInputChange('area', e.target.value)}
                    placeholder="e.g., 4500 sq ft"
                    required
                  />

                  <Input
                    label="Duration"
                    value={formData.duration}
                    onChange={(e) => handleInputChange('duration', e.target.value)}
                    placeholder="e.g., 8 months"
                    required
                  />

                  <Input
                    label="Budget"
                    value={formData.budget}
                    onChange={(e) => handleInputChange('budget', e.target.value)}
                    placeholder="e.g., ₹2.5 Cr"
                    required
                  />

                  <Input
                    label="Client Name"
                    value={formData.client}
                    onChange={(e) => handleInputChange('client', e.target.value)}
                    placeholder="Enter client name"
                    required
                  />

                  <Input
                    label="Completion Date"
                    value={formData.completionDate}
                    onChange={(e) => handleInputChange('completionDate', e.target.value)}
                    placeholder="YYYY-MM-DD"
                    type="date"
                  />

                  <Input
                    label="Rating"
                    value={formData.rating}
                    onChange={(e) => handleInputChange('rating', e.target.value)}
                    placeholder="1-5"
                    type="number"
                    min="1"
                    max="5"
                  />
                </div>

                <Input
                  label="Project Description"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Enter detailed project description"
                  multiline
                  rows={4}
                  required
                />

                {/* Image Upload Handlers */}
                <div className="space-y-4">
                  <label className="block text-sm font-medium text-gray-700">Featured Image</label>
                  <div className="flex items-start space-x-4">
                    <div className="relative w-32 h-20 bg-gray-100 rounded overflow-hidden border border-gray-200">
                      <img src={formData.image} alt="Featured" className="w-full h-full object-cover" />
                      {uploading && <div className="absolute inset-0 bg-black/50 flex items-center justify-center text-white text-xs">Uploading...</div>}
                    </div>
                    <div className="flex-1">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="block w-full text-sm text-gray-500
                              file:mr-4 file:py-2 file:px-4
                              file:rounded-full file:border-0
                              file:text-sm file:font-semibold
                              file:bg-custom-brown/10 file:text-custom-brown
                              hover:file:bg-custom-brown/20
                              cursor-pointer transition-colors"
                      />
                      <p className="mt-1 text-xs text-gray-500">Upload a high-quality image for the project thumbnail.</p>
                    </div>
                  </div>
                </div>

                <Input
                  label="Client Testimonial"
                  value={formData.testimonial}
                  onChange={(e) => handleInputChange('testimonial', e.target.value)}
                  placeholder="Enter client testimonial"
                  multiline
                  rows={3}
                />

                {/* Services */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Services Provided
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {serviceOptions.map((service) => (
                      <label key={service} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={formData.services.includes(service)}
                          onChange={() => handleServiceToggle(service)}
                          className="rounded border-gray-300 text-custom-brown focus:ring-custom-brown"
                        />
                        <span className="text-sm text-gray-700">{service}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Highlights */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Project Highlights
                    </label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      iconName="Plus"
                      iconPosition="left"
                      onClick={handleHighlightAdd}
                    >
                      Add Highlight
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {formData.highlights.map((highlight, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <span className="text-sm bg-gray-100 px-2 py-1 rounded flex-1 border border-gray-200">{highlight}</span>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          iconName="X"
                          onClick={() => handleHighlightRemove(index)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Gallery */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Gallery Images
                    </label>
                    <div className="relative">
                      <input
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={handleGalleryUpload}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        iconName="Plus"
                        iconPosition="left"
                      >
                        Add Images
                      </Button>
                    </div>
                  </div>
                  {uploading && <div className="text-sm text-custom-brown mb-2">Uploading gallery images...</div>}
                  <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                    {formData.gallery.map((image, index) => (
                      <div key={index} className="relative group">
                        <img src={image} alt={`Gallery ${index}`} className="w-full h-24 object-cover rounded border border-gray-200" />
                        <button
                          type="button"
                          onClick={() => handleGalleryRemove(index)}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                        >
                          <Icon name="X" size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 p-4 border-t border-gray-200 bg-gray-50 flex-shrink-0 rounded-b-lg">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCloseModal}
                  className="bg-white border-gray-300 hover:bg-gray-100"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="default"
                  iconName={editingProject ? 'Save' : 'Plus'}
                  iconPosition="left"
                  disabled={uploading}
                  className="bg-custom-brown hover:bg-custom-brown/90 text-white"
                >
                  {uploading ? 'Processing...' : (editingProject ? 'Update Project' : 'Add Project')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectsManager; 